/**
 * CampGeoHelper - Helper utilities for adding geolocation to camp operations
 * 
 * Use these helpers when creating or updating camps to ensure they have
 * proper geolocation data for efficient spatial queries.
 */

import LocationOps from './LocationOps';
import GeoUtils from './GeoUtils';
import GeocodeCache from './GeocodeCache';

class CampGeoHelper {
    /**
     * Enrich camp data with geolocation information
     * Call this before saving a camp to Firestore
     * 
     * @param campData - The camp object (with address fields)
     * @returns Camp data with geolocation field added
     */
    static async enrichCampWithGeolocation(campData: any): Promise<any> {
        try {
            // Build address from camp data
            const address = this.buildCampAddress(campData);
            
            if (!address) {
                console.warn('Cannot add geolocation: no address data provided');
                return campData;
            }

            // Get coordinates (with caching)
            const coords = await this.geocodeWithCache(address);
            
            if (!coords) {
                console.warn('Failed to geocode camp address:', address);
                return campData;
            }

            // Validate coordinates
            if (!GeoUtils.isValidCoordinates(coords.lat, coords.lng)) {
                console.warn('Invalid coordinates received:', coords);
                return campData;
            }

            // Create geolocation object
            const geolocation = GeoUtils.createLocationObject(coords.lat, coords.lng);

            // Return enriched camp data
            return {
                ...campData,
                geolocation,
                campAddress: address,
            };
        } catch (error) {
            console.error('Error enriching camp with geolocation:', error);
            return campData; // Return original data if error
        }
    }

    /**
     * Build full address string from camp data
     */
    static buildCampAddress(campData: any): string | null {
        // Check if campAddress already exists
        if (campData.campAddress && typeof campData.campAddress === 'string') {
            return campData.campAddress;
        }

        // Build from individual fields
        const parts = [
            campData.street,
            campData.city,
            campData.state,
            campData.zipCode,
        ].filter(part => part && typeof part === 'string' && part.trim() !== '');

        if (parts.length === 0) {
            return null;
        }

        return parts.join(', ');
    }

    /**
     * Geocode address with caching
     */
    private static async geocodeWithCache(address: string): Promise<{ lat: number; lng: number } | null> {
        // Check cache first
        const cached = GeocodeCache.get(address);
        if (cached) {
            return { lat: cached.lat, lng: cached.lng };
        }

        // Make API call
        const result = await LocationOps.getLocationFromAddress(address);
        
        if (result) {
            // Cache the result
            GeocodeCache.set(address, result.lat, result.lng);
            return result;
        }

        return null;
    }

    /**
     * Validate that a camp has proper geolocation data
     */
    static hasValidGeolocation(camp: any): boolean {
        if (!camp.geolocation) {
            return false;
        }

        const { lat, lng, geohash } = camp.geolocation;

        return (
            typeof lat === 'number' &&
            typeof lng === 'number' &&
            typeof geohash === 'string' &&
            GeoUtils.isValidCoordinates(lat, lng) &&
            geohash.length > 0
        );
    }

    /**
     * Extract coordinates from camp data
     * Returns null if camp doesn't have valid geolocation
     */
    static getCampCoordinates(camp: any): { lat: number; lng: number } | null {
        if (!this.hasValidGeolocation(camp)) {
            return null;
        }

        return {
            lat: camp.geolocation.lat,
            lng: camp.geolocation.lng,
        };
    }

    /**
     * Calculate distance between a camp and a location
     */
    static calculateDistanceToCamp(
        camp: any,
        targetLat: number,
        targetLng: number,
        inMiles: boolean = true
    ): number | null {
        const coords = this.getCampCoordinates(camp);
        
        if (!coords) {
            return null;
        }

        return GeoUtils.calculateDistance(
            targetLat,
            targetLng,
            coords.lat,
            coords.lng,
            inMiles
        );
    }

    /**
     * Format camp address for display
     */
    static formatCampAddress(camp: any): string {
        if (camp.campAddress) {
            return camp.campAddress;
        }

        const parts = [
            camp.street,
            camp.city,
            camp.state,
            camp.zipCode,
        ].filter(Boolean);

        return parts.join(', ') || 'Address not available';
    }
}

export default CampGeoHelper;

